import { AssessmentStepKeys } from '@/enums'
import { useFormContext } from '@/providers'
import { useSaveAssessment } from '@/services'
import {
  getLocalStorageItem,
  makeContext,
  mixpanelTrack,
  setLocalStorageItem,
} from '@/utils'
import {
  concat,
  flip,
  identity,
  keys,
  map,
  mergeDeepLeft,
  pick,
  pipe,
} from 'ramda'
import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useParams, useSearchParams } from 'react-router-dom'

export const useSaveAssessmentStep = (
  step: string,
  modifier: <T>(value: T) => T = identity,
  isYearlyAssessment?: boolean,
): {
  save: () => Promise<void>
  isLoading: boolean
} => {
  const { locationId } = useParams<{ locationId: string }>()
  const queryClient = useQueryClient()
  const { customValidate, values } = useFormContext()
  const { mutateAsync: saveAssessmentMutation, isLoading } = useSaveAssessment(
    locationId as string,
    isYearlyAssessment,
  )

  const save = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        customValidate({
          onSuccess: () => {
            if (!locationId) {
              mixpanelTrack(step)
              resolve()
            } else {
              saveAssessmentMutation({ [step]: modifier(values) })
                .then(() => {
                  mixpanelTrack(step)
                  if (step === AssessmentStepKeys.userProfile) {
                    queryClient.invalidateQueries('profile')
                  }
                  if (
                    step === AssessmentStepKeys.companyDetails ||
                    step === AssessmentStepKeys.powerOfAttorney
                  ) {
                    queryClient.invalidateQueries('companies')
                  }
                  resolve()
                })
                .catch(() => reject())
            }
          },
          onFail: reject,
        })
      }),
    [values],
  )

  return { save, isLoading }
}

// eslint-disable-next-line import/no-unused-modules
export const [AssessmentValuesProvider, useAssessmentValues] = makeContext()

export const makeSteps = (stepsObj: Record<string, any>) =>
  pipe(
    flip(pick)(stepsObj),
    keys,
    map((key) => ({ key, ...stepsObj[key] })),
  )

export const makeStepsWithBreadcrumbs = (
  stepsObj: Record<string, any>,
  stepKeys: Array<{ key: string; [key: string]: any }>,
) => stepKeys.map((step) => mergeDeepLeft(step, stepsObj[step.key]))

export const makeBusinessTypeSubmitValue = ({
  businessType,
  locationType,
  locationSubtypes = [],
  locationTypeSpecification,
}: {
  businessType: string
  locationType: string
  locationSubtypes?: string[]
  locationTypeSpecification?: string
}) => ({
  businessType,
  locationType:
    businessType === 'other' && !!locationTypeSpecification
      ? 'other'
      : locationType,
  locationSubtypes,
  locationTypeSpecification,
})

const makeStorageKey = flip(concat)

export const makeStoredCurrentStepKey = (makeStorageKey(
  // @ts-expect-error This will be fixed when removing ramda
  '-current-step',
) as unknown) as (arg: string) => string
// @ts-expect-error This will be fixed when removing ramda
export const makeStoredAssessmentValuesKey: (
  key: string,
  // @ts-expect-error This will be fixed when removing ramda
) => string = makeStorageKey('-values')

export const getStoredAssessmentValues = pipe(
  makeStoredAssessmentValuesKey,
  getLocalStorageItem,
)

export const getStoredCurrentStep = pipe(
  makeStoredCurrentStepKey,
  getLocalStorageItem,
)

export const setStoredAssessmentValues = (categoryId: string, value: any) => {
  setLocalStorageItem(makeStoredAssessmentValuesKey(categoryId), value)
}

export const setStoredCurrentStep = (categoryId: string, value: any) => {
  setLocalStorageItem(makeStoredCurrentStepKey(categoryId), value)
}

// This function will be deleted after the release of the new version
// of the tracking when funnel product is used instead
export const useForwardFunnelSource = () => {
  const [searchParams] = useSearchParams()
  const source = searchParams.get('funnel_source')

  return source ? { funnel_source: source } : {}
}

export const useForwardFunnelProduct = () => {
  const [searchParams] = useSearchParams()
  const product = searchParams.get('funnel_product')

  return product ? { funnel_product: product } : {}
}

export const useStepperWarning = (value: number, debounceTime = 1000) => {
  const [shouldShowWarning, setShouldShowWarning] = useState(false)
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, debounceTime)

    return () => {
      clearTimeout(handler)
    }
  }, [value, debounceTime])

  useEffect(() => {
    if (debouncedValue && debouncedValue < 9000) {
      setShouldShowWarning(true)
    } else {
      setShouldShowWarning(false)
    }
  }, [debouncedValue])

  return {
    shouldShowWarning,
  }
}

export const makeIgnoredStepsKey = (categoryId: string) =>
  `${categoryId}-ignored-steps`
